import * as THREE from './three'
import { ROOMS } from '../../server/rooms'
import { disposeRoomGraphics } from './disposeRoomGraphics'
import { endXRSession } from './endXRSession'

export function doorDestination(roomId, leafId) {
  if (roomId !== 'reception') return 'reception'
  const number = Number(leafId.match(/(\d{3})$/)?.[1])
  if ([5, 6].includes(number)) return 'meetingroom'
  if ([1, 2].includes(number)) return 'showroom'
  if ([3, 4].includes(number)) return 'auditorium'
  return null
}

export function roomURL(id) {
  const room = ROOMS.find(room => room.id === id)
  return room ? `https://${room.hostname}` : null
}

export class DoorPortals {
  constructor(world, doors, root) {
    this.root = root
    this.world = world
    this.doors = doors
    this.offset = new THREE.Vector3()
    this.traveling = false
    this.configure()
  }

  async configure() {
    try {
      const response = await fetch('/health')
      if (!response.ok) return
      const health = await response.json()
      const roomId = health.roomId || 'reception'
      for (const door of this.doors) {
        const first = door.leaves[0].node
        door.destination = doorDestination(roomId, first.id)
        if (!door.destination) continue
        // The leaves lie in their local X/Z plane (Blender -> glTF).
        door.inverse = first.matrixWorld.clone().invert()
      }
      const names = { meeting_thumb: 'meetingroom', showroom_thumb: 'showroom',
        auditorium_thumb: 'auditorium', reception_thumb: 'reception' }
      const panels = []
      this.root.traverse(node => {
        if (names[node.id] && node._geometry) panels.push(node)
      })
      for (const panel of panels) {
        const destination = names[panel.id]
        const originalTexture = await this.world.loader.load('texture', `/room-thumbs/${destination}-1440.webp`)
        const texture = originalTexture.clone()
        texture.repeat.x = -1
        texture.offset.x = 1
        texture.needsUpdate = true
        const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, toneMapped: false })
        panel.linked = false
        panel.castShadow = false
        panel.receiveShadow = false
        panel.material = material
      }
    } catch (error) {
      console.error('Door preview unavailable:', error)
    }
  }

  update() {
    if (this.traveling || this.world.roomTravelPending) return
    const player = this.world.entities.player
    if (!player?.base) return
    for (const door of this.doors) {
      if (!door.inverse || !door.destination) continue
      const local = this.offset.copy(player.base.position).applyMatrix4(door.inverse)
      const side = Math.abs(local.y) > 0.08 ? Math.sign(local.y) : 0
      const previous = door.previous
      door.previous = { x: local.x, y: local.y, z: local.z, side: side || previous?.side || 0 }
      if (!side || !previous?.side || side === previous.side) continue
      // Only a continuous crossing inside the doorway, never a spawn/teleport.
      const distance = Math.hypot(local.x - previous.x, local.y - previous.y, local.z - previous.z)
      if (distance > 2 || Math.abs(local.x) > 1.5 || Math.abs(local.z) > 2.6 || door.amount < 0.5) continue
      this.traveling = true
      this.travel(door.destination)
      return
    }
  }

  travel(destination) {
    return travelToRoom(this.world, destination)
  }
}

// Shared transfer for doors and explicit scene buttons. Keep the XR exit,
// cleanup and room-transfer allowlist in one path.
export async function travelToRoom(world, destination) {
  if (world.roomTravelPending || !roomURL(destination)) return
  world.roomTravelPending = true
  const session = world.xr?.session
  const xrEnd = await endXRSession(session)
  if (session) console.info('[room-travel] xr session', xrEnd)
  // Release the old world before Safari creates the next room's WebGL context.
  const renderer = world.graphics.renderer
  console.info('[room-travel] graphics resources', {
    textures: renderer.info.memory.textures,
    geometries: renderer.info.memory.geometries,
    programs: renderer.info.programs?.length || 0,
  })
  const releases = [
    () => world.client.destroy(),
    () => world.network.destroy(),
    () => world.livekit?.destroy(),
    () => world.courseRoom?.destroy(),
    () => world.receptionist?.destroy(),
    () => world.audio.destroy(),
    () => console.info('[room-travel] disposed', disposeRoomGraphics(world)),
    () => renderer.dispose(),
    // Never pull the context from under a session the headset still owns.
    () => xrEnd !== 'timeout' && renderer.forceContextLoss(),
  ]
  for (const release of releases) {
    try { release() } catch (error) { console.error('Room cleanup failed:', error) }
  }
  window.addEventListener('pageshow', event => {
    if (event.persisted) window.location.reload()
  }, { once: true })
  window.location.replace(`/room-transfer.html?room=${encodeURIComponent(destination)}`)
}
